<h2 mat-dialog-title>
  @if (reportType === AdminReportType.Executive) {
    {{ 'admin.scans.generateExecutiveReport.title' | translate }}
  } @else if (reportType === AdminReportType.Full) {
    {{ 'admin.scans.generateFullReport.title' | translate }}
  } @else {
    {{ 'admin.scans.generateReport.title' | translate }}
  }
</h2>
<mat-dialog-content>
  <form [formGroup]="formGroup" class="form-container">
    <mat-form-field appearance="outline">
      <mat-label>Company</mat-label>
      <input matInput formControlName="companyName" [matAutocomplete]="auto" />
      <mat-autocomplete #auto="matAutocomplete" [displayWith]="displayClientFn">
        @for (account of filteredClients(); track $index) {
          <mat-option [value]="account">
            {{ account.companyName }}
          </mat-option>
        }
      </mat-autocomplete>
      @if (formGroup.get('companyName')?.hasError('required')) {
        <mat-error>{{ 'common.errors.required' | translate }}</mat-error>
      }
      @if (formGroup.get('companyName')?.hasError('invalidClient')) {
        <mat-error>{{ 'admin.scans.errors.invalidClient' | translate }}</mat-error>
      }
    </mat-form-field>

    @if (reportType !== AdminReportType.Executive) {
      <mat-form-field appearance="outline">
        <mat-label>{{ 'admin.scans.scanName' | translate }}</mat-label>
        <input matInput formControlName="scanName" />
        @if (formGroup.get('scanName')?.hasError('required')) {
          <mat-error>{{ 'common.errors.required' | translate }}</mat-error>
        }
      </mat-form-field>
    }

    <mat-form-field appearance="outline">
      <mat-label>{{ 'admin.scans.language' | translate }}</mat-label>
      <mat-select formControlName="language">
        @for (lang of languages; track lang) {
          <mat-option [value]="lang">{{ lang }}</mat-option>
        }
      </mat-select>
    </mat-form-field>

    @if (reportType !== AdminReportType.Executive) {
      <div class="toggle-container">
        <mat-checkbox formControlName="isc">
          {{ 'admin.scans.isc' | translate }}
        </mat-checkbox>
      </div>
      @if (formGroup.get('isc')?.value != true) {
        <mat-form-field appearance="outline">
          <mat-label>{{ 'admin.scans.threshold' | translate }}</mat-label>
          <input matInput type="number" step="0.1" formControlName="threshold" />
          @if (formGroup.get('threshold')?.hasError('required')) {
            <mat-error>{{ 'common.errors.required' | translate }}</mat-error>
          }
        </mat-form-field>
      } @else {
        <div class="form-row" formGroupName="reducedConfiguration">
          <mat-form-field appearance="outline">
            <mat-label>{{ 'admin.scans.reduced.numInternalFindings' | translate }}</mat-label>
            <input matInput type="number" step="1" formControlName="numInternalFindings" />
            @if (formGroup.get('reducedConfiguration.numInternalFindings')?.hasError('required')) {
              <mat-error>{{ 'common.errors.required' | translate }}</mat-error>
            }
          </mat-form-field>
          <mat-form-field appearance="outline">
            <mat-label>{{ 'admin.scans.reduced.numExternalFindings' | translate }}</mat-label>
            <input matInput type="number" step="1" formControlName="numExternalFindings" />
            @if (formGroup.get('reducedConfiguration.numExternalFindings')?.hasError('required')) {
              <mat-error>{{ 'common.errors.required' | translate }}</mat-error>
            }
          </mat-form-field>
        </div>
      }
    }

    <mat-form-field appearance="outline">
      <mat-label>{{ 'admin.scans.horizonKey' | translate }}</mat-label>
      <input matInput formControlName="horizonApiKey" />
      @if (formGroup.get('horizonApiKey')?.hasError('invalidApiKey')) {
        <mat-error>{{ 'admin.scans.errors.invalidApiKey' | translate }}</mat-error>
      }
    </mat-form-field>

    <div class="modules-container">
      <h3>{{ 'admin.scans.modules' | translate }}</h3>

      <div formGroupName="horizonInternal" class="module-group">
        <mat-checkbox formControlName="enabled">
          {{ 'admin.scans.moduleTypes.horizonInternal' | translate }}
        </mat-checkbox>
        @if (formGroup.get('horizonInternal.enabled')?.value) {
          <div class="module-fields">
            <mat-form-field appearance="outline" class="scan-field">
              <mat-label>{{ 'admin.scans.scan' | translate }}</mat-label>
              <input type="text" matInput formControlName="scan" [matAutocomplete]="autoInternal" />
              <mat-autocomplete #autoInternal="matAutocomplete" [displayWith]="displayScanFn">
                @for (scan of filteredInternalScans(); track scan.opId) {
                  <mat-option [value]="scan" class="scan-option-item">
                    <dbx-admin-horizon-scan-option [scan]="scan"></dbx-admin-horizon-scan-option>
                  </mat-option>
                }
              </mat-autocomplete>
              @if (isScansLoading()) {
                <mat-spinner matSuffix diameter="20"></mat-spinner>
              }
              @if (formGroup.get('horizonInternal.scan')?.hasError('required')) {
                <mat-error>{{ 'admin.scans.errors.internalScanRequired' | translate }}</mat-error>
              }
            </mat-form-field>
            @if (reportType !== AdminReportType.Executive) {
              <mat-form-field appearance="outline">
                <mat-label>{{ 'admin.scans.compareTo' | translate }}</mat-label>
                <input type="text" matInput formControlName="compareTo" [matAutocomplete]="autoInternalCompare" />
                <mat-autocomplete #autoInternalCompare="matAutocomplete" [displayWith]="displayScanFn">
                  @for (scan of comparableInternalScans(); track scan.opId) {
                    <mat-option [value]="scan" class="scan-option-item">
                      <dbx-admin-horizon-scan-option [scan]="scan"></dbx-admin-horizon-scan-option>
                    </mat-option>
                  }
                </mat-autocomplete>
              </mat-form-field>
            }
          </div>
        }
      </div>

      <div formGroupName="horizonExternal" class="module-group">
        <mat-checkbox formControlName="enabled">
          {{ 'admin.scans.moduleTypes.horizonExternal' | translate }}
        </mat-checkbox>
        @if (formGroup.get('horizonExternal.enabled')?.value) {
          <div class="module-fields">
            <mat-form-field appearance="outline" class="scan-field">
              <mat-label>{{ 'admin.scans.scan' | translate }}</mat-label>
              <input type="text" matInput formControlName="scan" [matAutocomplete]="autoExternal" />
              <mat-autocomplete #autoExternal="matAutocomplete" [displayWith]="displayScanFn">
                @for (scan of filteredExternalScans(); track scan.opId) {
                  <mat-option [value]="scan" class="scan-option-item">
                    <dbx-admin-horizon-scan-option [scan]="scan"></dbx-admin-horizon-scan-option>
                  </mat-option>
                }
              </mat-autocomplete>
              @if (isScansLoading()) {
                <mat-spinner matSuffix diameter="20"></mat-spinner>
              }
              @if (formGroup.get('horizonExternal.scan')?.hasError('required')) {
                <mat-error>{{ 'admin.scans.errors.externalScanRequired' | translate }}</mat-error>
              }
            </mat-form-field>
            @if (reportType !== AdminReportType.Executive) {
              <mat-form-field appearance="outline">
                <input type="text" matInput formControlName="compareTo" [matAutocomplete]="autoExternalCompare" />
                <mat-autocomplete #autoExternalCompare="matAutocomplete" [displayWith]="displayScanFn">
                  @for (scan of comparableExternalScans(); track scan.opId) {
                    <mat-option [value]="scan" class="scan-option-item">
                      <dbx-admin-horizon-scan-option [scan]="scan"></dbx-admin-horizon-scan-option>
                    </mat-option>
                  }
                </mat-autocomplete>
              </mat-form-field>
            }
          </div>
        }
      </div>

      <div formGroupName="nessus" class="module-group">
        <mat-checkbox formControlName="enabled">
          {{ 'admin.scans.moduleTypes.nessus' | translate }}
        </mat-checkbox>
        @if (formGroup.get('nessus.enabled')?.value) {
          <div class="module-fields">
            <mat-form-field appearance="outline" class="scan-field">
              <mat-label>{{ 'admin.scans.scan' | translate }}</mat-label>
              <input type="text" matInput formControlName="scan" [matAutocomplete]="autoNessusScan" />
              <mat-autocomplete #autoNessusScan="matAutocomplete" [displayWith]="displayNessusScanFn">
                @for (config of sortedNessusConfigs().likely; track config.id) {
                  <mat-option [value]="config">{{ config.name }} (id: {{ config.id }})</mat-option>
                }
                @if (sortedNessusConfigs().likely.length > 0 && sortedNessusConfigs().unlikely.length > 0) {
                  <mat-divider style="margin: 4px 0"></mat-divider>
                }
                @for (config of sortedNessusConfigs().unlikely; track config) {
                  <mat-option [value]="config">{{ config.name }} (id: {{ config.id }})</mat-option>
                }
              </mat-autocomplete>
              @if (isNessusScansLoading()) {
                <mat-spinner matSuffix diameter="20"></mat-spinner>
              }
              @if (formGroup.get('nessus.scan')?.hasError('required')) {
                <mat-error>{{ 'common.errors.required' | translate }}</mat-error>
              }
            </mat-form-field>
            <mat-form-field appearance="outline" class="scan-field">
              <mat-label>{{ 'admin.scans.historyId' | translate }}</mat-label>
              <input type="text" matInput formControlName="history" [matAutocomplete]="autoNessusHistory" />
              <mat-autocomplete #autoNessusHistory="matAutocomplete" [displayWith]="displayNessusHistoryFn">
                @for (history of filteredNessusHistory(); track history.historyId) {
                  <mat-option [value]="history" class="scan-option-item">
                    <dbx-admin-nessus-scan-history-option [history]="history"></dbx-admin-nessus-scan-history-option>
                  </mat-option>
                }
              </mat-autocomplete>
              @if (isNessusHistoryLoading()) {
                <mat-spinner matSuffix diameter="20"></mat-spinner>
              }
              @if (formGroup.get('nessus.history')?.hasError('required')) {
                <mat-error>{{ 'common.errors.required' | translate }}</mat-error>
              }
            </mat-form-field>
            @if (reportType !== AdminReportType.Executive) {
              <mat-form-field appearance="outline">
                <mat-label>{{ 'admin.scans.compareTo' | translate }}</mat-label>
                <input type="text" matInput formControlName="compareTo" [matAutocomplete]="autoNessusHistoryCompare" />
                <mat-autocomplete #autoNessusHistoryCompare="matAutocomplete" [displayWith]="displayNessusHistoryFn">
                  @for (history of comparableNessusHistory(); track history.historyId) {
                    <mat-option [value]="history" class="scan-option-item">
                      <dbx-admin-nessus-scan-history-option [history]="history"></dbx-admin-nessus-scan-history-option>
                    </mat-option>
                  }
                </mat-autocomplete>
              </mat-form-field>
            }
          </div>
        }
      </div>

      @if (reportType !== AdminReportType.Executive) {
        <div formGroupName="breachLense" class="module-group">
          <mat-checkbox formControlName="enabled">
            {{ 'admin.scans.moduleTypes.breachLense' | translate }}
          </mat-checkbox>
          @if (formGroup.get('breachLense.enabled')?.value) {
            <div class="module-fields">
              <mat-form-field appearance="outline">
                <mat-label>{{ 'admin.scans.domain' | translate }}</mat-label>
                <input matInput formControlName="domain" />
              </mat-form-field>
            </div>
          }
        </div>
      }
    </div>
    @if (
      formGroup.hasError('noModuleEnabled') &&
      (formGroup.get('horizonInternal.enabled')?.touched || formGroup.get('horizonExternal.enabled')?.touched)
    ) {
      <mat-error>{{ 'admin.scans.errors.noModuleEnabled' | translate }}</mat-error>
    }
  </form>
</mat-dialog-content>
<mat-dialog-actions align="end">
  <button mat-button (click)="close()" [disabled]="isLoading">{{ 'common.cancel' | translate }}</button>
  <button mat-flat-button color="primary" (click)="save()" [disabled]="formGroup.invalid || isLoading">
    <span class="loading-button-container">
      <span [style.visibility]="isLoading ? 'hidden' : 'visible'">
        {{ 'common.generate' | translate }}
      </span>
      @if (isLoading) {
        <mat-spinner diameter="20"></mat-spinner>
      }
    </span>
  </button>
</mat-dialog-actions>
